import React from 'react'
import { css } from '@emotion/core'
import { string } from 'prop-types'
import { defaultFills } from '../../pages/KidsHome/BackgroundFills'
import { BackgroundFillsType } from '../../sharedPropTypes/activityProps'

// Generate a deterministic-ish animation config for each cloud on module load.
// We keep the pairing of delay + duration together (previously they were generated independently)
// to avoid unnatural motion patterns and layout jitter on weaker devices.
// If callers explicitly pass null (or a non-number) for base or multi, fall back to sane defaults
// instead of allowing implicit coercion (e.g., null * 1.5 => 0) which would collapse duration.
const randAnime = (base = 120, multi = 10) => {
  const validNumber = (v, fallback) => (typeof v === 'number' && !Number.isNaN(v) ? v : fallback)
  const normBase = validNumber(base, 120)
  const normMulti = validNumber(multi, 10)
  const factor = Math.max(1, Math.ceil(normMulti * Math.random())) // ensure non-zero delay
  return { duration: 1.5 * normBase + factor, delay: factor }
}

// Precompute once so values remain stable for the session (avoids re-calculation during re-renders)
const cloudConfigs = [
  randAnime(), // cloud-1
  randAnime(100), // cloud-2
  randAnime(), // cloud-3
  randAnime(150, 6), // cloud-4
  randAnime(), // cloud-5
  randAnime(200), // cloud-6
]

// Helper to safely access config (fallback keeps animation working even if array changes)
const cfg = (i) => cloudConfigs[i] || { duration: 180, delay: 0 }

const landingBgCss = css`
  /*
    Performance-oriented tweaks:
    - Use translate3d & will-change to hint GPU acceleration.
    - Linear timing for constant velocity (reduces perceived stutter compared to default ease).
    - Keyframes use viewport-based end position so we don't hard-cap at 1200px on wider screens.
    - Stable delay/duration pairings (precomputed) to avoid reflow recalculations.
    - Respect prefers-reduced-motion to disable non-essential animation for accessibility & perf.
  */
  @keyframes cloud-drift {
    0% {
      transform: translate3d(-1300px, 0, 0);
    }
    100% {
      /* Move fully across plus extra buffer to avoid pop-in on wrap */
      transform: translate3d(calc(100vw + 1300px), 0, 0);
    }
  }

  position: absolute;
  width: 100%;
  height: 100%;

  g,
  svg {
    transition: opacity 0.3s ease-in-out;
  }
  path {
    transition: fill 0.3s ease-in-out;
  }

  .cloud {
    will-change: transform;
    /* Start off-screen to the left matching the first keyframe position */
    transform: translate3d(-1300px, 0, 0);
    animation-name: cloud-drift;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
  }
  /* Dynamically insert per-cloud timings */
  .cloud-1 {
    animation-delay: ${cfg(0).delay}s;
    animation-duration: ${cfg(0).duration}s;
  }
  .cloud-2 {
    animation-delay: ${cfg(1).delay}s;
    animation-duration: ${cfg(1).duration}s;
  }
  .cloud-3 {
    animation-delay: ${cfg(2).delay}s;
    animation-duration: ${cfg(2).duration}s;
  }
  .cloud-4 {
    animation-delay: ${cfg(3).delay}s;
    animation-duration: ${cfg(3).duration}s;
  }
  .cloud-5 {
    animation-delay: ${cfg(4).delay}s;
    animation-duration: ${cfg(4).duration}s;
  }
  .cloud-6 {
    animation-delay: ${cfg(5).delay}s;
    animation-duration: ${cfg(5).duration}s;
  }

  /* Reduce animation cost for users requesting less motion */
  @media (prefers-reduced-motion: reduce) {
    .cloud {
      animation: none;
      transform: translate3d(0, 0, 0);
      opacity: 0.15; /* subtle hint of clouds without motion */
    }
  }
`

export default function LandingPageBackground({ fillColors = defaultFills, height = '100%' }) {
  return (
    <div
      css={landingBgCss}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        height,
      }}
    >
      <svg
        role="presentation"
        viewBox="0 0 1440 422"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: '100%', maxWidth: '1440px', minWidth: '1240px', height: height ?? 'auto' }}
      >
        <g clipPath="url(#clip0)">
          <rect width="1440" height="422" fill="url(#paint0_linear)" />
          <path
            className="cloud cloud-1"
            d="M1216.96 227.274C1217.73 223.765 1220.05 220.714 1223.37 218.866C1226.69 217.017 1230.7 216.54 1234.42 217.55C1237.52 209.726 1246.58 205.389 1255.29 207.556C1264 209.724 1269.48 217.678 1267.91 225.886C1270.53 223.071 1281 223.071 1281 232H1206C1206 228.429 1211.85 224.988 1216.96 227.274Z"
            fill={fillColors.light05}
          />
          <path
            className="cloud cloud-2"
            d="M178.155 199.121C181.633 199.123 184.849 200.864 186.614 203.697C188.087 201.306 191.259 200.391 193.893 201.598C194.434 199.071 196.072 196.874 198.407 195.543C200.742 194.212 203.561 193.869 206.177 194.596C208.364 188.963 214.738 185.84 220.867 187.401C226.995 188.961 230.849 194.688 229.747 200.598C230.347 200.446 230.966 200.367 231.588 200.365C233.967 200.361 236.2 201.449 237.579 203.282C238.026 203.172 238.487 203.116 238.949 203.115C239.319 203.114 239.688 203.149 240.05 203.219C241.328 201.571 243.494 200.763 245.619 201.143C247.743 201.524 249.447 203.024 250 205H169C170.488 201.451 174.117 199.121 178.155 199.121Z"
            fill={fillColors.light05}
          />
          <path
            className="cloud cloud-3"
            d="M564.234 168.219C563.619 165.412 561.757 162.971 559.102 161.492C556.447 160.013 553.242 159.632 550.267 160.44C547.781 154.181 540.534 150.711 533.566 152.445C526.598 154.179 522.216 160.542 523.469 167.109C521.372 164.857 513 164.857 513 172H573C573 169.143 568.32 166.39 564.234 168.219Z"
            fill={fillColors.light}
          />
          <path
            className="cloud cloud-4"
            d="M862.155 146.121C865.633 146.123 868.849 147.864 870.614 150.697C872.087 148.306 875.259 147.391 877.893 148.598C878.434 146.071 880.072 143.874 882.407 142.543C884.742 141.212 887.561 140.869 890.177 141.596C892.364 135.963 898.738 132.84 904.867 134.401C910.995 135.961 914.849 141.688 913.747 147.598C914.347 147.446 914.966 147.367 915.588 147.365C917.967 147.361 920.2 148.449 921.579 150.282C922.026 150.172 922.487 150.116 922.949 150.115C923.319 150.114 923.688 150.149 924.05 150.219C925.328 148.571 927.494 147.763 929.619 148.143C931.743 148.524 933.447 150.024 934 152H853C854.488 148.451 858.117 146.121 862.155 146.121Z"
            fill={fillColors.light}
          />
          <path
            className="cloud cloud-5"
            d="M564.234 168.219C563.619 165.412 561.757 162.971 559.102 161.492C556.447 160.013 553.242 159.632 550.267 160.44C547.781 154.181 540.534 150.711 533.566 152.445C526.598 154.179 522.216 160.542 523.469 167.109C521.372 164.857 513 164.857 513 172H573C573 169.143 568.32 166.39 564.234 168.219Z"
            fill={fillColors.light}
          />
          <path
            className="cloud cloud-6"
            d="M178.155 199.121C181.633 199.123 184.849 200.864 186.614 203.697C188.087 201.306 191.259 200.391 193.893 201.598C194.434 199.071 196.072 196.874 198.407 195.543C200.742 194.212 203.561 193.869 206.177 194.596C208.364 188.963 214.738 185.84 220.867 187.401C226.995 188.961 230.849 194.688 229.747 200.598C230.347 200.446 230.966 200.367 231.588 200.365C233.967 200.361 236.2 201.449 237.579 203.282C238.026 203.172 238.487 203.116 238.949 203.115C239.319 203.114 239.688 203.149 240.05 203.219C241.328 201.571 243.494 200.763 245.619 201.143C247.743 201.524 249.447 203.024 250 205H169C170.488 201.451 174.117 199.121 178.155 199.121Z"
            fill={fillColors.light05}
          />
          <path
            d="M0 230.61C0 230.61 179.5 268.049 268.5 267C357.5 265.951 371 216.72 457 230.61C543 244.5 612 209 724.5 230.61C837 252.221 867.5 230.61 959.5 230.61C1051.5 230.61 1138.47 260.891 1234 273.307C1329.53 285.724 1440 244.5 1440 244.5V327.307H0V230.61Z"
            fill={fillColors.dark15}
          />
          <path
            d="M1440 270.393C1440 270.393 1399.97 266.569 1311 265.176C1222.03 263.782 1163.74 228.351 1089.12 265.176C1014.5 302 848 237 722.5 253C597 269 389.5 270.393 308 253C226.5 235.607 196.48 276.859 140 283.5C83.5195 290.14 -5 259.958 -5 259.958V370H1440V270.393Z"
            fill={fillColors.lessDark}
          />
          <path
            d="M0 311.332C0 311.332 51 255.798 126 277C201 298.202 327 294.66 461 320C595 345.34 720 259 920 290C1120 321 1228 295 1305 283.5C1382 272 1445 300.681 1445 300.681V413H0V311.332Z"
            fill={fillColors.midLight}
          />
          <path
            d="M0 301.303C0 301.303 76.5 316.241 165.5 315.193C254.5 314.144 310.609 274.212 457.16 301.303C603.711 328.393 649.472 267.758 730.5 289.5C811.528 311.242 942.328 324.631 1037.36 301.303C1132.39 277.975 1138.47 331.584 1234 344C1329.53 356.416 1440 315.193 1440 315.193V398H0V301.303Z"
            fill={fillColors.light}
          />
          <path
            d="M-5 367.393C-5 367.393 40.4843 329.389 129.453 327.995C218.423 326.602 226.92 338.934 345.879 362.176C464.837 385.417 618.284 316.671 819.215 322.333C1020.15 327.995 1095.12 361.227 1183.09 367.393C1271.06 373.559 1279.06 327.55 1335.54 334.191C1392.02 340.831 1440 356.958 1440 356.958V467H0.498097L-5 367.393Z"
            fill={fillColors.base}
          />
          <path
            d="M864 230.259C864 239.312 865.335 239.573 867.018 239.573V206.814C865.364 206.814 864 221.206 864 230.259Z"
            fill={fillColors.dark15}
          />
          <path
            d="M867.018 206.814V239.573C868.672 239.573 870.036 239.283 870.036 230.259C870.036 221.235 868.672 206.814 867.018 206.814Z"
            fill={fillColors.dark15}
          />
          <path
            d="M93 246.331C93 255.384 94.3347 255.645 96.0176 255.645V222.886C94.3637 222.886 93 237.278 93 246.331Z"
            fill={fillColors.dark15}
          />
          <path
            d="M96.0178 222.886V255.645C97.6717 255.645 99.0355 255.355 99.0355 246.331C99.0355 237.307 97.6717 222.886 96.0178 222.886Z"
            fill={fillColors.dark15}
          />
          <path
            d="M876.1 228.344C876.1 237.397 877.434 237.658 879.117 237.658V204.928C877.434 204.928 876.1 219.32 876.1 228.344Z"
            fill={fillColors.dark15}
          />
          <path
            d="M879.118 204.928V237.687C880.772 237.687 882.135 237.397 882.135 228.373C882.135 219.349 880.772 204.928 879.118 204.928Z"
            fill={fillColors.dark15}
          />
          <path
            d="M105.1 244.416C105.1 253.469 106.434 253.73 108.117 253.73V221C106.434 221 105.1 235.392 105.1 244.416Z"
            fill={fillColors.dark15}
          />
          <path
            d="M108.118 221V253.759C109.772 253.759 111.135 253.469 111.135 244.445C111.135 235.421 109.772 221 108.118 221Z"
            fill={fillColors.dark15}
          />
          <path
            d="M888.374 227.416C888.374 236.469 889.709 236.73 891.392 236.73V204C889.738 204 888.374 218.363 888.374 227.416Z"
            fill={fillColors.dark15}
          />
          <path
            d="M891.391 204V236.759C893.045 236.759 894.409 236.469 894.409 227.445C894.409 218.363 893.074 204 891.391 204Z"
            fill={fillColors.dark15}
          />
          <rect width="500" height="422" fill="url(#paint1_linear)" />
          <rect x="940" width="500" height="422" fill="url(#paint2_linear)" />
          <path
            d="M909.724 331.761C891.803 343.531 869.389 341.078 869.389 341.078L934.293 298.457C934.306 298.452 927.645 319.991 909.724 331.761Z"
            fill={fillColors.base}
          />
          <path
            d="M934.303 298.452L869.399 341.074C869.399 341.074 876.046 319.539 893.98 307.764C911.914 295.99 934.303 298.452 934.303 298.452Z"
            fill={fillColors.base}
          />
          <path
            d="M896.222 308.999C886.081 327.892 865.228 336.427 865.228 336.427L901.925 268C901.912 268 906.349 290.092 896.222 308.999Z"
            fill={fillColors.base}
          />
          <path
            d="M901.913 268L865.215 336.427C865.215 336.427 860.778 314.321 870.918 295.428C881.059 276.535 901.913 268 901.913 268Z"
            fill={fillColors.base}
          />
          <path
            d="M523.554 258.384C523.554 258.384 520.066 249.974 524.283 239.732C528.499 229.489 520.287 224.744 515.983 228.033C511.7 231.322 509.405 242.8 523.554 258.384Z"
            fill={fillColors.base}
          />
          <path
            d="M531.479 264.741C531.479 264.741 534.15 256.044 543.95 250.835C553.729 245.625 550.44 236.752 545.032 236.531C539.602 236.332 530.529 243.705 531.479 264.741Z"
            fill={fillColors.base}
          />
          <path
            d="M530.53 265.646C530.53 265.646 521.436 265.293 513.29 272.82C505.145 280.347 497.861 274.299 499.45 269.112C501.039 263.947 510.995 257.81 530.53 265.646Z"
            fill={fillColors.base}
          />
          <path
            d="M542.361 280.767C542.361 280.767 533.267 280.414 525.122 287.941C516.977 295.468 509.692 289.42 511.282 284.232C512.871 279.045 522.826 272.931 542.361 280.767Z"
            fill={fillColors.base}
          />
          <path
            d="M549.469 297.057C549.469 297.057 540.374 296.704 532.229 304.231C524.084 311.758 516.8 305.71 518.389 300.523C519.978 295.357 529.934 289.221 549.469 297.057Z"
            fill={fillColors.base}
          />
          <path
            d="M557.371 317.586C557.371 317.586 548.277 317.233 540.132 324.76C531.986 332.287 524.702 326.239 526.291 321.051C527.881 315.886 537.836 309.75 557.371 317.586Z"
            fill={fillColors.base}
          />
          <path
            d="M542.228 280.656C542.228 280.656 544.899 271.959 554.7 266.75C564.479 261.541 561.19 252.667 555.782 252.446C550.351 252.247 541.279 259.62 542.228 280.656Z"
            fill={fillColors.base}
          />
          <path
            d="M550.925 298.558C550.925 298.558 553.596 289.861 563.397 284.652C573.176 279.442 569.887 270.569 564.479 270.348C559.049 270.149 549.976 277.522 550.925 298.558Z"
            fill={fillColors.base}
          />
          <path
            d="M558.012 317.21C558.012 317.21 560.682 308.513 570.483 303.304C580.262 298.095 576.973 289.221 571.565 289C566.135 288.824 557.062 296.196 558.012 317.21Z"
            fill={fillColors.base}
          />
          <path
            d="M568.982 363.102H567.084C566.421 358.312 565.605 353.257 564.589 347.981C559.336 320.808 547.526 282.665 520.972 255.205C520.618 254.83 520.618 254.234 520.994 253.881C521.369 253.528 521.965 253.528 522.318 253.903C549.182 281.694 561.146 320.235 566.444 347.672C567.481 353.058 568.32 358.246 568.982 363.102Z"
            fill={fillColors.base}
          />
          <path
            d="M466.868 279.641C466.868 279.641 462.189 274.608 462.498 266.264C462.785 257.92 455.743 256.728 453.558 260.172C451.373 263.637 452.785 272.335 466.868 279.641Z"
            fill={fillColors.base}
          />
          <path
            d="M474.132 282.047C474.132 282.047 473.734 275.204 479.297 268.957C484.837 262.71 480.18 257.302 476.295 258.561C472.41 259.841 467.929 267.434 474.132 282.047Z"
            fill={fillColors.base}
          />
          <path
            d="M473.69 282.952C473.69 282.952 467.178 285.093 463.403 292.554C459.629 300.015 452.874 297.653 452.631 293.569C452.388 289.486 457.841 282.533 473.69 282.952Z"
            fill={fillColors.base}
          />
          <path
            d="M486.029 290.523C486.029 290.523 479.517 292.664 475.743 300.125C471.968 307.586 465.214 305.224 464.971 301.141C464.75 297.057 470.18 290.104 486.029 290.523Z"
            fill={fillColors.base}
          />
          <path
            d="M495.322 300.17C495.322 300.17 488.81 302.311 485.035 309.772C481.261 317.232 474.506 314.871 474.264 310.787C474.043 306.703 479.473 299.772 495.322 300.17Z"
            fill={fillColors.base}
          />
          <path
            d="M506.314 312.619C506.314 312.619 499.803 314.76 496.028 322.221C492.253 329.682 485.499 327.32 485.256 323.237C485.035 319.153 490.465 312.2 506.314 312.619Z"
            fill={fillColors.base}
          />
          <path
            d="M485.918 290.479C485.918 290.479 485.521 283.636 491.083 277.389C496.624 271.143 491.966 265.734 488.081 266.993C484.196 268.273 479.715 275.866 485.918 290.479Z"
            fill={fillColors.base}
          />
          <path
            d="M496.756 300.854C496.756 300.854 496.359 294.011 501.922 287.764C507.462 281.517 502.804 276.109 498.92 277.367C495.035 278.648 490.554 286.241 496.756 300.854Z"
            fill={fillColors.base}
          />
          <path
            d="M506.667 312.178C506.667 312.178 506.27 305.335 511.833 299.088C517.373 292.841 512.716 287.433 508.831 288.691C504.946 289.949 500.465 297.565 506.667 312.178Z"
            fill={fillColors.base}
          />
          <path
            d="M537.747 368.819C537.394 368.907 537.019 368.708 536.908 368.355C536.842 368.179 531.169 351.557 519.404 332.198C508.566 314.363 490.178 290.501 464.22 278.052C463.866 277.875 463.712 277.456 463.889 277.102C464.065 276.749 464.485 276.595 464.838 276.771C491.128 289.375 509.692 313.48 520.64 331.47C532.494 350.961 538.211 367.715 538.255 367.87C538.387 368.245 538.189 368.642 537.813 368.775C537.791 368.797 537.769 368.819 537.747 368.819Z"
            fill={fillColors.base}
          />
        </g>
        <defs>
          <linearGradient id="paint0_linear" x1="720" y1="0" x2="719.851" y2="158.118" gradientUnits="userSpaceOnUse">
            <stop stopColor={fillColors.light02} />
            <stop offset="1" stopColor={fillColors.base} />
          </linearGradient>
          <linearGradient id="paint1_linear" x1="0" y1="0" x2="500" y2="-1.67562e-06" gradientUnits="userSpaceOnUse">
            <stop offset="0.0677083" stopColor={fillColors.base} />
            <stop offset="1" stopColor={fillColors.base} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="paint2_linear" x1="940" y1="0" x2="1440" y2="-1.67562e-06" gradientUnits="userSpaceOnUse">
            <stop stopColor={fillColors.base} stopOpacity="0" />
            <stop offset="0.885417" stopColor={fillColors.base} />
          </linearGradient>
          <clipPath id="clip0">
            <rect width="1440" height="422" fill={fillColors.base} />
          </clipPath>
        </defs>
      </svg>
    </div>
  )
}

LandingPageBackground.propTypes = {
  height: string,
  fillColors: BackgroundFillsType,
}
